import { normalizeStoreAlias, type Address, PaymentTypeEnum } from "shared-components";
import { createWithEqualityFn } from "zustand/traditional";

import type { CreateCard } from "@/api/card/models";
import type { CheckoutPaymentInfo, ShippingMethod } from "@/api/cart/models";
import type { Coupon } from "@/api/coupon/models";

export interface CheckoutCreditCard extends CreateCard {
  id: string;
  lastDigits: string;
  brand: string;
}

export interface CheckoutPayment extends Omit<CheckoutPaymentInfo, "card"> {
  card?: CheckoutCreditCard;
}

export interface ShoppingCartStore {
  // Alias da loja que inicializou formulário de checkout atual
  storeAlias?: string;

  // Seleções do usuário para checkout
  selectedPaymentMethod: CheckoutPayment;
  selectedShippingAddress?: Address & { id: string };
  selectedShippingMethod?: ShippingMethod;
  appliedCoupon?: Coupon;

  // Cache local necessário
  savedAddresses: (Address & { id: string })[];
  savedCards: CheckoutCreditCard[];

  // Actions para UI state
  resetForStore: (storeAlias?: string) => void;
  setSelectedShippingAddress: (address?: Address & { id: string }) => void;
  setSelectedPaymentMethod: (method: CheckoutPayment) => void;
  setSelectedShippingMethod: (method?: ShippingMethod) => void;
  setAppliedCoupon: (coupon?: Coupon) => void;
  addSavedAddress: (address: Address & { id: string }) => void;
  addSavedCard: (card: CheckoutCreditCard) => void;
  clearCheckoutState: () => void;
}

/** Initial state - apenas UI selections */
const initialCheckoutState = () => ({
  selectedShippingAddress: undefined,
  selectedPaymentMethod: { type: PaymentTypeEnum.CREDIT_CARD },
  selectedShippingMethod: undefined,
  appliedCoupon: undefined,
  savedAddresses: [],
  savedCards: [],
});

export const useShoppingCartStore = createWithEqualityFn<ShoppingCartStore>((set) => ({
  ...initialCheckoutState(),
  resetForStore: (storeAlias) => {
    const normalizedStoreAlias = normalizeStoreAlias(storeAlias);

    set((currentState) => {
      if (currentState.storeAlias === normalizedStoreAlias) {
        return currentState;
      }

      return {
        ...initialCheckoutState(),
        storeAlias: normalizedStoreAlias,
      };
    });
  },

  // Actions - apenas para UI state
  setSelectedShippingAddress: (selectedShippingAddress) => set({ selectedShippingAddress }),

  setSelectedPaymentMethod: (selectedPaymentMethod) => set({ selectedPaymentMethod }),

  setSelectedShippingMethod: (selectedShippingMethod) => set({ selectedShippingMethod }),

  setAppliedCoupon: (appliedCoupon) => set({ appliedCoupon }),

  addSavedAddress: (address) => set((currentState) => ({ savedAddresses: [...currentState.savedAddresses, address] })),

  addSavedCard: (card) => set((currentState) => ({ savedCards: [...currentState.savedCards, card] })),

  clearCheckoutState: () => set(initialCheckoutState()),
}));
